import {
	ChangeEventHandler,
	FocusEventHandler,
	forwardRef,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import { InputInstance, InputProps } from "./PropsTypes";
import { usePropsValue } from "@/hooks/usePropsValue";
import { formatNumber, resetScroll } from "./utils";
import clsx from "clsx";

const Input = forwardRef<InputInstance, InputProps>((props, ref) => {
	const [inputFocus, setInputFocus] = useState(false);
	const compositionStartRef = useRef(false);
	const [value, setValue] = usePropsValue(props);
	const inputRef = useRef<HTMLInputElement>(null);

	const { type, placeholder, maxLength, readOnly, disabled } = props;

	useImperativeHandle(ref, () => ({
		clear: () => {
			setValue("");
		},
		focus,
		blur,
		get nativeElement() {
			return inputRef.current;
		},
	}));

	const showClear = useMemo(() => {
		if (props.clearable && !readOnly) {
			const hasValue = value !== "";
			return hasValue && inputFocus;
		}
		return false;
	}, [value, inputFocus]);

	const focus = () => {
		if (inputRef?.current) {
			inputRef.current.focus();
		}
	};
	const blur = () => {
		if (inputRef?.current) {
			inputRef.current.blur();
		}
	};

	const handleValueChange: ChangeEventHandler<HTMLInputElement> = (e) => {
		let finalValue = e?.currentTarget?.value;

		if (!compositionStartRef.current) {
			const isDef = maxLength !== undefined && maxLength !== null;
			if (isDef && finalValue.length > +(maxLength || 0)) {
				finalValue = finalValue.slice(0, maxLength);
				props.onOverLimit?.();
			}

			if (type === "number" || type === "digit") {
				const isNumber = type === "number";
				finalValue = formatNumber(finalValue, isNumber, isNumber);
			}
		}

		setValue(finalValue);
	};

	const handleFocus: FocusEventHandler<HTMLInputElement> = (e) => {
		setInputFocus(true);
		props.onFocus?.(e);

		// readOnly not work in legacy mobile safari
		if (readOnly) {
			blur();
		}

		if (props.align === "right") {
			// 光标位置到最后
			setTimeout(() => {
				const input = inputRef.current;
				if (input) {
					const length = input.value.length;
					input.setSelectionRange(length, length);
				}
			}, 0);
		}
	};

	const handleBlur: FocusEventHandler<HTMLInputElement> = (e) => {
		setInputFocus(false);
		props.onBlur?.(e);
		resetScroll();
	};

	const renderInput = () => {
		let inputType = type;
		let inputMode = props.inputMode;

		if (type === "number") {
			inputType = "text";
			inputMode = "decimal";
		}

		if (type === "digit") {
			inputType = "tel";
			inputMode = "numeric";
		}

		return (
			<input
				ref={inputRef}
				type={inputType}
				name={props.name}
				inputMode={inputMode || "text"}
				value={value}
				placeholder={placeholder}
				readOnly={readOnly}
				disabled={disabled}
				onBlur={handleBlur}
				onFocus={handleFocus}
				onChange={handleValueChange}
				onInput={props.onInput}
				autoFocus={props.autoFocus}
				onCompositionStart={(_) => {
					compositionStartRef.current = true;
				}}
				onCompositionEnd={(e) => {
					compositionStartRef.current = false;
					handleValueChange(e as any);
				}}
				onClick={props.onClick}
				className={clsx("input_inner", props.innerClassName)}
			/>
		);
	};

	// styles
	const useMemoCn = useMemo(() => {
		let sizeCn = "";
		switch (props.size) {
			case "md":
				sizeCn = "input_wrapper_size_md";
				break;
			case "lg":
			default:
				sizeCn = "input_wrapper_size_lg";
		}

		let variantCn = "";
		switch (props.variant) {
			case "underline":
				{
					variantCn = "input_wrapper_variants_underline px-4.5";
					if (props.disabled) {
						variantCn = clsx(variantCn, "input_wrapper_variants_underline__disabled");
					} else {
						if (inputFocus) {
							variantCn = clsx(variantCn, "input_wrapper_variants_underline__active");
						} else {
							variantCn = clsx(variantCn, "input_wrapper_variants_underline__normal");
						}
					}
				}
				break;
			case "standard":
			default: {
				variantCn = "input_wrapper_variants_standard";
				if (props.disabled) {
					variantCn = clsx(variantCn, "input_wrapper_variants_standard__disabled");
				} else {
					if (inputFocus) {
						variantCn = clsx(variantCn, "input_wrapper_variants_standard__active");
					} else {
						variantCn = clsx(variantCn, "input_wrapper_variants_standard__normal");
					}
				}
			}
		}

		let alignCn = "";
		switch (props.align) {
			case "center":
				alignCn = "text-center";
				break;
			case "right":
				alignCn = "text-right";
				break;
			case "left":
			default:
				alignCn = "text-left";
		}
		return clsx("group input_wrapper", sizeCn, variantCn, alignCn, props.className);
	}, [inputFocus, props.size, props.variant, props.disabled, props.clearable, props.className]);

	return (
		<div className={useMemoCn} data-focus={inputFocus ? "true" : "false"}>
			{props.label && (
				<div className={clsx("mr-2", inputFocus ? "text-dark dark:text-f1ece8" : "c3_85_text")}>{props.label}</div>
			)}

			{props.leading}
			{renderInput()}

			{props.trailing}
		</div>
	);
});

Input.displayName = "Input";
export default Input;
